import { getRelativeTime } from '@thoughtspot/date-format-util';
import i18n from '@thoughtspot/i18n';
import { getCurrentOrgId } from '@thoughtspot/session-service';
import { strings } from '@thoughtspot/translations';
import _ from 'lodash';
import { getErrorCodeFromGraphQLErrors } from './error-handling/error-utils';
import { create } from './logger';
const logger = create('chart-customization-util');
export const DEFAULT_ORG_ID = -1;
export var sortType;
(function (sortType) {
    sortType["ASC"] = "asc";
    sortType["DESC"] = "desc";
})(sortType || (sortType = {}));
export var DATA_TEST_ID;
(function (DATA_TEST_ID) {
    DATA_TEST_ID["DISPLAY_NAME"] = "display-name";
    DATA_TEST_ID["DISPLAY_NAME_CONTAINER"] = "display-name-container";
    DATA_TEST_ID["FILE_PICKER"] = "file-picker";
    DATA_TEST_ID["URL_SOURCE"] = "url-source";
    DATA_TEST_ID["FILE_PICKER_CONTAINER"] = "file-picker-container";
    DATA_TEST_ID["SOURCE_PICKER"] = "source-picker";
    DATA_TEST_ID["PROPERTY_SELECTOR_CONTAINER"] = "property-selector-container";
    DATA_TEST_ID["PROPERTY_SELECTOR"] = "property-selector";
    DATA_TEST_ID["FILE_FETCH_SOURCE_LOCAL_RADIO"] = "radio_LOCAL";
    DATA_TEST_ID["FILE_FETCH_SOURCE_REMOTE_RADIO"] = "radio_remote";
    DATA_TEST_ID["MAP_PREVIEW_LOADER"] = "custom-region-map-loading";
    DATA_TEST_ID["GEO_CONFIG_HELPER_DOC_LINK_TEST_ID"] = "geo-config-helper-doc-link";
})(DATA_TEST_ID || (DATA_TEST_ID = {}));
export const GEO_CONFIG_HELPER_DOC_LINK = '/latest/data-modeling-geo-data#_how_to_edit_geographic_columns';
export const COL_TO_DATA_MAPPER = {
    mapName: 'name',
    uploadedBy: 'modifiedByName',
    lastModified: 'modified',
};
const ERROR_CODE_TEST_ID_MAP = {
    10606: DATA_TEST_ID.DISPLAY_NAME,
    13047: DATA_TEST_ID.FILE_PICKER_CONTAINER,
};
export const isFileEditAllowed = (userOrgId, customHeader) => {
    if (_.isNil(userOrgId))
        return true;
    if (userOrgId === DEFAULT_ORG_ID)
        return true;
    if (!!customHeader.belongToAllOrgs && userOrgId !== DEFAULT_ORG_ID)
        return false;
    const orgIds = customHeader.orgIds;
    return _.isArray(orgIds) && orgIds.includes(userOrgId);
};
export const getNormalizedData = (data, sortOrder, handleRowEdit, handleOnDelete, currentOrgId) => {
    if (!data?.length)
        return [];
    const userOrgId = currentOrgId === DEFAULT_ORG_ID ? currentOrgId : getCurrentOrgId();
    const sortCol = sortOrder.find((col) => col?.colId);
    let normalizedData = [...data];
    if (sortCol?.sort) {
        const sortKey = COL_TO_DATA_MAPPER[sortCol.colId];
        normalizedData = _.orderBy(normalizedData, [sortKey], [sortCol.sort]);
    }
    return normalizedData.map((row) => ({
        id: row.id,
        mapName: { text: row.name },
        uploadedBy: [
            {
                name: row.modifiedByName,
                id: row.modifiedBy,
            },
        ],
        lastModified: { text: getRelativeTime(row.modified) },
        actions: {
            dataTestIdSuffix: row.name,
            editKey: row.id,
            deleteKey: row.id,
            disableUpdateMsg: isFileEditAllowed(userOrgId, row)
                ? null
                : _.get(strings, 'chartCustomization.geomaps.customRegion.update.disabled.orgBlocker'),
            rowEditFunction: () => handleRowEdit(row.id),
            rowDeleteFunction: () => handleOnDelete(row.id, row.name),
        },
    }));
};
export const getPropertyKeysFromTopojson = (topoJsonFileContent, onErrorCallback) => {
    try {
        const parsedFileContent = JSON.parse(topoJsonFileContent);
        const customMapObjects = parsedFileContent.objects;
        const properties = customMapObjects[Object.keys(customMapObjects)[0]].geometries[0]
            .properties;
        const propertyKeys = Object.entries(properties)
            .filter(object => typeof object[1] === 'string' ||
            typeof object[1] === 'number')
            .map(object => object[0]);
        return propertyKeys;
    }
    catch (err) {
        onErrorCallback();
        logger.warn('error while parsing the file', err);
        return [];
    }
};
export const getSanitisedTopoJsoncontent = (customRegionFileContent, selectedProperty, onErrorCallback) => {
    try {
        let parsedFileContent = JSON.parse(customRegionFileContent);
        const customMapObjects = parsedFileContent.objects;
        const selectedLayer = Object.keys(customMapObjects)[0];
        const selectedObject = customMapObjects[selectedLayer];
        const valueList = [];
        const geometries = [];
        const actualSize = selectedObject.geometries.length;
        for (let index = 0; index < actualSize; index++) {
            valueList.push(String(selectedObject.geometries[index].properties[selectedProperty]));
            geometries.push({
                ...selectedObject.geometries[index],
                properties: {
                    name: String(selectedObject.geometries[index].properties[selectedProperty]),
                },
            });
        }
        const uniqueValuesSize = new Set(valueList).size;
        if (actualSize !== uniqueValuesSize) {
            onErrorCallback(true, false);
            return null;
        }
        parsedFileContent = {
            ...parsedFileContent,
            objects: { [selectedLayer]: { ...selectedObject, geometries } },
        };
        return JSON.stringify(parsedFileContent);
    }
    catch (err) {
        onErrorCallback(false, true);
        logger.warn('error while parsing the file', err);
    }
    return null;
};
export const handleCustomRegionMutationError = (error, callback) => {
    const errorCode = getErrorCodeFromGraphQLErrors(error);
    const defaultMessage = 'error.validation.default';
    const errorMessage = i18n.exists(`customRegion.error_message.${errorCode}`)
        ? `customRegion.error_message.${errorCode}`
        : defaultMessage;
    callback(_.get(strings, errorMessage), ERROR_CODE_TEST_ID_MAP[errorCode] || DATA_TEST_ID.FILE_PICKER_CONTAINER);
};
export const convertBytesToMb = (sizeInBytes, decimalPrecision = 2) => {
    const kiloBytes = sizeInBytes / 1024;
    const megaBytes = kiloBytes / 1024;
    return Number(megaBytes.toFixed(decimalPrecision));
};
//# sourceMappingURL=chart-customization.js.map